import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Users,
  MessageSquare,
  Mail,
  Download,
  RefreshCw,
  Package,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Warehouse,
  Tag,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { API, LOGO_URL, downloadCsv, fmtChf } from "./utils";
import ContentStudio from "./ContentStudio";
import ImageStudio from "./ImageStudio";
import WarehouseStudio from "./WarehouseStudio";
import DiscountCodes from "./DiscountCodes";

const Dashboard = () => {
  const { user, token, logout } = useAuth();
  const [tab, setTab] = useState("signups");
  const [signups, setSignups] = useState([]);
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [s, m, o, st] = await Promise.all([
        axios.get(`${API}/admin/prelaunch`, { headers }),
        axios.get(`${API}/admin/contact`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setSignups(s.data);
      setMessages(m.data);
      setOrders(o.data);
      setStats(st.data);
    } catch (err) {
      toast.error("Fehler beim Laden");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = () => {
    logout();
    toast.success("Abgemeldet");
    navigate("/");
  };

  const toggleCustoms = async (orderId, newValue) => {
    // Optimistic UI update
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId ? { ...o, customs_covered_by_seller: newValue } : o
      )
    );
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/customs-toggle`,
        { customs_covered_by_seller: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        newValue
          ? "DDP aktiv — 4keeperz übernimmt Einfuhr/Zoll"
          : "DDP deaktiviert — Empfänger trägt Einfuhr/Zoll"
      );
    } catch (err) {
      // Revert on failure
      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, customs_covered_by_seller: !newValue } : o
        )
      );
      toast.error("Konnte DDP nicht ändern");
      console.error(err);
    }
  };

  const exportSignups = () =>
    downloadCsv(
      `4keeperz-prelaunch-${new Date().toISOString().slice(0, 10)}.csv`,
      signups,
      ["email", "name", "interest", "size", "discount_code", "created_at"]
    );

  const exportMessages = () =>
    downloadCsv(
      `4keeperz-contact-${new Date().toISOString().slice(0, 10)}.csv`,
      messages,
      ["name", "email", "subject", "message", "created_at"]
    );

  const exportOrders = () =>
    downloadCsv(
      `4keeperz-orders-${new Date().toISOString().slice(0, 10)}.csv`,
      orders.map((o) => ({
        id: o.id,
        email: o.email,
        customer_name: o.customer_name,
        items: (o.line_items || [])
          .map((li) => `${li.name}${li.size ? " " + li.size : ""} x${li.quantity}`)
          .join(" | "),
        subtotal_chf: o.subtotal_chf,
        discount_code: o.discount_code || "",
        discount_amount_chf: o.discount_amount_chf || 0,
        bundle_discount_chf: o.bundle_discount_chf || 0,
        shipping_chf: o.shipping_chf,
        total_chf: o.total_chf,
        created_at: o.created_at,
      })),
      [
        "id",
        "email",
        "customer_name",
        "items",
        "subtotal_chf",
        "discount_code",
        "discount_amount_chf",
        "bundle_discount_chf",
        "shipping_chf",
        "total_chf",
        "created_at",
      ]
    );

  const revenueTotal = orders.reduce((sum, o) => sum + Number(o.total_chf || 0), 0);

  const tabs = [
    { id: "signups", label: `Prelaunch (${signups.length})`, icon: null },
    { id: "messages", label: `Kontakt (${messages.length})`, icon: null },
    { id: "orders", label: `Bestellungen (${orders.length})`, icon: null },
    { id: "content", label: "Content Studio", icon: Sparkles },
    { id: "warehouse", label: "Lager & Umsatz", icon: Warehouse },
    { id: "discounts", label: "Rabattcodes", icon: Tag },
  ];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0A0A0A] sticky top-0 z-10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src={LOGO_URL}
              alt="4keeperz"
              className="h-12 md:h-14 w-auto object-contain"
              data-testid="admin-dashboard-logo"
            />
            <span className="hidden md:inline font-condensed uppercase tracking-widest text-xs text-white/40 border-l border-white/15 pl-4">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchAll}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#00A651] hover:text-[#00A651] text-white/70 font-condensed uppercase tracking-widest text-xs px-4 py-2 transition-colors"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden md:inline">Refresh</span>
            </button>
            <span className="hidden md:inline text-sm text-white/50">{user?.email}</span>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#00A651] hover:text-[#00A651] text-white/70 font-condensed uppercase tracking-widest text-xs px-4 py-2 transition-colors"
            >
              <LogOut className="w-3 h-3" />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-12 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="stat-signups">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[#00A651]" />
              <span className="font-condensed uppercase tracking-widest text-xs text-white/50">
                Prelaunch
              </span>
            </div>
            <div className="mt-3 font-display text-5xl text-[#00A651] leading-none">
              {stats?.prelaunch_signups ?? "—"}
            </div>
          </div>
          <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="stat-messages">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-[#00A651]" />
              <span className="font-condensed uppercase tracking-widest text-xs text-white/50">
                Kontakt
              </span>
            </div>
            <div className="mt-3 font-display text-5xl text-[#00A651] leading-none">
              {stats?.contact_messages ?? "—"}
            </div>
          </div>
          <div
            className="border border-white/10 bg-[#0A0A0A] p-6 col-span-2 md:col-span-1"
            data-testid="stat-orders"
          >
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-[#00A651]" />
              <span className="font-condensed uppercase tracking-widest text-xs text-white/50">
                Orders · Umsatz
              </span>
            </div>
            <div className="mt-3 flex items-baseline gap-4">
              <div className="font-display text-5xl text-[#00A651] leading-none">
                {orders.length}
              </div>
              <div className="font-condensed text-sm text-white/60">{fmtChf(revenueTotal)}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div
          className="flex flex-wrap gap-[1px] bg-white/10 border border-white/10 mb-6 w-fit"
          data-testid="admin-tabs"
        >
          {tabs.map((t) => {
            const active = tab === t.id;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`px-5 py-3 font-condensed uppercase tracking-widest text-xs transition-colors flex items-center gap-1.5 ${
                  active ? "bg-[#00A651] text-black" : "bg-black text-white/60 hover:text-white"
                }`}
              >
                {Icon && <Icon className="w-3 h-3" />}
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {tab === "signups" && (
          <section data-testid="signups-section">
            <div className="flex justify-end mb-4">
              <button
                onClick={exportSignups}
                disabled={!signups.length}
                data-testid="export-signups"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#00A651] hover:text-[#00A651] text-white/70 font-condensed uppercase tracking-widest text-xs px-4 py-2 transition-colors disabled:opacity-40"
              >
                <Download className="w-3 h-3" />
                CSV Export
              </button>
            </div>
            {signups.length === 0 ? (
              <div className="border border-dashed border-white/10 p-16 text-center text-white/50">
                Noch keine Anmeldungen.
              </div>
            ) : (
              <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-left" data-testid="signups-table">
                  <thead className="bg-black/60 border-b border-white/10">
                    <tr>
                      {["E-Mail", "Name", "Interesse", "Größe", "Code", "Datum"].map((h, i) => (
                        <th
                          key={i}
                          className="font-condensed uppercase tracking-widest text-[11px] text-white/50 px-4 py-3"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {signups.map((s) => (
                      <tr
                        key={s.id}
                        className="border-b border-white/5 hover:bg-black/40 transition-colors"
                        data-testid={`signup-row-${s.id}`}
                      >
                        <td className="px-4 py-3 text-[#00A651]">
                          <a
                            href={`mailto:${s.email}`}
                            className="hover:underline inline-flex items-center gap-2"
                          >
                            <Mail className="w-3 h-3" />
                            {s.email}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-white/80">{s.name || "—"}</td>
                        <td className="px-4 py-3 text-white/60 text-sm">{s.interest || "—"}</td>
                        <td className="px-4 py-3 text-white/80 text-sm font-display">
                          {s.size || "—"}
                        </td>
                        <td className="px-4 py-3 text-white/60 text-sm font-mono uppercase">
                          {s.discount_code || "—"}
                        </td>
                        <td className="px-4 py-3 text-white/50 text-sm whitespace-nowrap">
                          {s.created_at
                            ? new Date(s.created_at).toLocaleString("de-CH")
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {tab === "messages" && (
          <section data-testid="messages-section">
            <div className="flex justify-end mb-4">
              <button
                onClick={exportMessages}
                disabled={!messages.length}
                data-testid="export-messages"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#00A651] hover:text-[#00A651] text-white/70 font-condensed uppercase tracking-widest text-xs px-4 py-2 transition-colors disabled:opacity-40"
              >
                <Download className="w-3 h-3" />
                CSV Export
              </button>
            </div>
            {messages.length === 0 ? (
              <div className="border border-dashed border-white/10 p-16 text-center text-white/50">
                Noch keine Nachrichten.
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((m) => (
                  <article
                    key={m.id}
                    data-testid={`message-row-${m.id}`}
                    className="border border-white/10 bg-[#0A0A0A] p-6"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="font-display text-xl text-white uppercase">{m.name}</div>
                        <a
                          href={`mailto:${m.email}`}
                          className="text-[#00A651] hover:underline inline-flex items-center gap-2 text-sm"
                        >
                          <Mail className="w-3 h-3" />
                          {m.email}
                        </a>
                      </div>
                      <div className="text-xs font-condensed uppercase tracking-widest text-white/40 whitespace-nowrap">
                        {m.created_at ? new Date(m.created_at).toLocaleString("de-CH") : "—"}
                      </div>
                    </div>
                    {m.subject && (
                      <div className="font-condensed uppercase tracking-widest text-xs text-white/50 mb-2">
                        Betreff: {m.subject}
                      </div>
                    )}
                    <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "orders" && (
          <section data-testid="orders-section">
            <div className="flex justify-end mb-4">
              <button
                onClick={exportOrders}
                disabled={!orders.length}
                data-testid="export-orders"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#00A651] hover:text-[#00A651] text-white/70 font-condensed uppercase tracking-widest text-xs px-4 py-2 transition-colors disabled:opacity-40"
              >
                <Download className="w-3 h-3" />
                CSV Export
              </button>
            </div>
            {orders.length === 0 ? (
              <div
                className="border border-dashed border-white/10 p-16 text-center text-white/50"
                data-testid="orders-empty"
              >
                Noch keine Bestellungen.
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => {
                  const expanded = expandedOrder === o.id;
                  return (
                    <article
                      key={o.id}
                      data-testid={`order-row-${o.id}`}
                      className="border border-white/10 bg-[#0A0A0A]"
                    >
                      <button
                        onClick={() => setExpandedOrder(expanded ? null : o.id)}
                        className="w-full text-left px-5 py-4 flex flex-wrap items-center gap-4 hover:bg-black/40 transition-colors"
                      >
                        {expanded ? (
                          <ChevronDown className="w-4 h-4 text-[#00A651] shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-white/40 shrink-0" />
                        )}
                        <div className="font-mono text-xs text-white/40 whitespace-nowrap">
                          #{o.id.slice(0, 8)}
                        </div>
                        <div className="flex-1 min-w-[140px]">
                          <div className="text-white font-condensed uppercase tracking-wider text-sm">
                            {o.customer_name || "—"}
                          </div>
                          <div className="text-[#00A651] text-xs flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {o.email}
                          </div>
                        </div>
                        <div className="text-white/60 text-xs font-condensed uppercase tracking-widest whitespace-nowrap">
                          {(o.line_items || []).reduce((s, li) => s + li.quantity, 0)} Artikel
                        </div>
                        <div className="font-display text-xl text-[#00A651] whitespace-nowrap">
                          {fmtChf(o.total_chf)}
                        </div>
                        <div className="text-xs text-white/40 font-condensed uppercase tracking-widest whitespace-nowrap">
                          {o.created_at ? new Date(o.created_at).toLocaleString("de-CH") : "—"}
                        </div>
                      </button>
                      {expanded && (
                        <div
                          className="border-t border-white/10 px-5 py-5 bg-black/40 space-y-4"
                          data-testid={`order-details-${o.id}`}
                        >
                          <div>
                            <div className="font-condensed uppercase tracking-widest text-[10px] text-white/40 mb-2">
                              Artikel
                            </div>
                            <div className="space-y-1.5">
                              {(o.line_items || []).map((li, idx) => (
                                <div key={idx} className="flex justify-between items-center text-sm">
                                  <div>
                                    <span className="text-white">{li.name}</span>
                                    {li.size && (
                                      <span className="text-white/50 ml-2 text-xs">
                                        · Größe {li.size}
                                      </span>
                                    )}
                                    <span className="text-white/40 ml-2 text-xs">
                                      × {li.quantity}
                                    </span>
                                  </div>
                                  <span className="text-white/70 font-display">
                                    {fmtChf(li.line_total_chf)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-white/10 pt-4 text-xs">
                            <div>
                              <div className="text-white/40 font-condensed uppercase tracking-widest mb-1">
                                Subtotal
                              </div>
                              <div className="text-white/90 font-display">
                                {fmtChf(o.subtotal_chf)}
                              </div>
                            </div>
                            <div>
                              <div className="text-white/40 font-condensed uppercase tracking-widest mb-1">
                                Rabatt
                              </div>
                              <div className="text-white/90 font-display">
                                {o.discount_amount_chf > 0
                                  ? `−${fmtChf(o.discount_amount_chf)}`
                                  : "—"}
                                {o.discount_code && (
                                  <span className="text-[#00A651] font-mono text-[10px] ml-1">
                                    {o.discount_code}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="text-white/40 font-condensed uppercase tracking-widest mb-1">
                                Bundle
                              </div>
                              <div className="text-white/90 font-display">
                                {o.bundle_discount_chf > 0
                                  ? `−${fmtChf(o.bundle_discount_chf)}`
                                  : "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-white/40 font-condensed uppercase tracking-widest mb-1">
                                Versand
                              </div>
                              <div className="text-white/90 font-display">
                                {fmtChf(o.shipping_chf)}
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-between items-center border-t border-white/10 pt-3 gap-3 flex-wrap">
                            <span className="font-condensed uppercase tracking-widest text-xs text-white/60">
                              Gesamt
                              {o.invoice_number && (
                                <span className="text-white/40 ml-2 font-mono normal-case tracking-normal">
                                  · {o.invoice_number}
                                </span>
                              )}
                            </span>
                            <div className="flex items-center gap-3 flex-wrap justify-end">
                              {o.region === "eu" && (
                                <div
                                  className="flex items-center gap-2.5 border border-white/10 hover:border-[#00A651]/60 transition-colors px-3 py-2 bg-black/50"
                                  data-testid={`ddp-toggle-wrap-${o.id}`}
                                  title="DDP · 4keeperz trägt Einfuhrumsatzsteuer und Zollgebühren für diese Bestellung"
                                >
                                  <Switch
                                    id={`ddp-${o.id}`}
                                    checked={!!o.customs_covered_by_seller}
                                    onCheckedChange={(v) => toggleCustoms(o.id, v)}
                                    data-testid={`ddp-toggle-${o.id}`}
                                    className="data-[state=checked]:bg-[#00A651]"
                                  />
                                  <label
                                    htmlFor={`ddp-${o.id}`}
                                    className={`font-condensed uppercase tracking-widest text-[11px] cursor-pointer select-none whitespace-nowrap ${
                                      o.customs_covered_by_seller
                                        ? "text-[#00A651]"
                                        : "text-white/60"
                                    }`}
                                  >
                                    DDP · 4keeperz zahlt Zoll
                                  </label>
                                </div>
                              )}
                              <span className="font-display text-2xl text-[#00A651]">
                                {fmtChf(o.total_chf)}
                              </span>
                              <a
                                href={`${API}/admin/orders/${o.id}/invoice`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  // Attach token as query since <a> doesn't send Authorization header
                                  e.preventDefault();
                                  fetch(`${API}/admin/orders/${o.id}/invoice`, {
                                    headers: { Authorization: `Bearer ${token}` },
                                  })
                                    .then((r) => {
                                      if (!r.ok) throw new Error("PDF fehlgeschlagen");
                                      return r.blob();
                                    })
                                    .then((blob) => {
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement("a");
                                      a.href = url;
                                      a.download = `4keeperz_${o.invoice_number || o.id.slice(0, 8)}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      a.remove();
                                      URL.revokeObjectURL(url);
                                    })
                                    .catch(() => alert("PDF konnte nicht geladen werden."));
                                }}
                                data-testid={`invoice-download-${o.id}`}
                                className="inline-flex items-center gap-2 border border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-black font-condensed uppercase tracking-widest text-[11px] px-3 py-2 transition-colors"
                              >
                                <Download className="w-3.5 h-3.5" />
                                Rechnung
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {tab === "content" && (
          <>
            <ContentStudio />
            <ImageStudio />
          </>
        )}

        {tab === "warehouse" && <WarehouseStudio />}
        {tab === "discounts" && <DiscountCodes />}
      </main>
    </div>
  );
};

export default Dashboard;
